import { create } from 'zustand';
import { 
  DashboardState, 
  Dashboard, 
  DashboardWidget, 
  CreateDashboardRequest, 
  UpdateDashboardRequest 
} from '@/types/dashboard';

interface DashboardStore extends DashboardState {
  setDashboards: (dashboards: Dashboard[]) => void;
  setCurrentDashboard: (dashboard: Dashboard | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  
  loadDashboards: () => Promise<void>;
  loadDashboard: (id: string) => Promise<void>;
  createDashboard: (data: CreateDashboardRequest) => Promise<Dashboard>;
  updateDashboard: (id: string, data: UpdateDashboardRequest) => Promise<Dashboard>;
  deleteDashboard: (id: string) => Promise<void>;
  duplicateDashboard: (id: string) => Promise<Dashboard>;
  
  addWidget: (widget: DashboardWidget) => void;
  updateWidget: (widgetId: string, updates: Partial<DashboardWidget>) => void;
  removeWidget: (widgetId: string) => void;
  moveWidget: (widgetId: string, position: { x: number; y: number }) => void;
  resizeWidget: (widgetId: string, size: { w: number; h: number }) => void;
  
  saveDashboard: () => Promise<void>;
  resetDashboard: () => void;
}

export const useDashboardStore = create<DashboardStore>((set, get) => ({
  dashboards: [],
  currentDashboard: null,
  isLoading: false,
  error: null,

  setDashboards: (dashboards) => set({ dashboards }),
  setCurrentDashboard: (dashboard) => set({ currentDashboard: dashboard }),
  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),

  loadDashboards: async () => {
    set({ isLoading: true, error: null });
    try {
      // TODO: Implement actual API call

      // Mock data
      const mockDashboards: Dashboard[] = [
        {
          id: 'dashboard-1',
          user_id: 'user-123',
          tenant_id: 'tenant-123',
          name: 'Sales Performance',
          description: 'Track sales metrics and KPIs',
          layout: {
            widgets: [],
            grid_settings: {
              cols: 12,
              rows: 20,
              gap: 16,
              padding: 16,
              responsive_breakpoints: [],
            },
          },
          is_public: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
        {
          id: 'dashboard-2',
          user_id: 'user-123',
          tenant_id: 'tenant-123',
          name: 'User Analytics',
          description: 'User engagement and behavior analysis',
          layout: {
            widgets: [],
            grid_settings: {
              cols: 12,
              rows: 20,
              gap: 16,
              padding: 16,
              responsive_breakpoints: [],
            },
          },
          is_public: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
      ];
      
      set({ dashboards: mockDashboards, isLoading: false });
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to load dashboards', isLoading: false });
    }
  },

  loadDashboard: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      // TODO: Implement actual API call
      const dashboard = get().dashboards.find(d => d.id === id);
      if (dashboard) {
        set({ currentDashboard: dashboard, isLoading: false });
      } else {
        throw new Error('Dashboard not found');
      }
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to load dashboard', isLoading: false });
    }
  },

  createDashboard: async (data: CreateDashboardRequest) => {
    set({ isLoading: true, error: null });
    try {
      // TODO: Implement actual API call
      const newDashboard: Dashboard = {
        id: `dashboard-${Date.now()}`,
        user_id: 'user-123',
        tenant_id: 'tenant-123',
        name: data.name,
        description: data.description,
        layout: {
          widgets: [],
          grid_settings: {
            cols: 12,
            rows: 20,
            gap: 16,
            padding: 16,
            responsive_breakpoints: [],
          },
        },
        is_public: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      
      set(state => ({
        dashboards: [...state.dashboards, newDashboard],
        currentDashboard: newDashboard,
        isLoading: false,
      }));
      
      return newDashboard;
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to create dashboard', isLoading: false });
      throw error;
    }
  },

  updateDashboard: async (id: string, data: UpdateDashboardRequest) => {
    set({ isLoading: true, error: null });
    try {
      // TODO: Implement actual API call
      const updatedDashboard = get().dashboards.find(d => d.id === id);
      if (!updatedDashboard) {
        throw new Error('Dashboard not found');
      }
      
      const updated = {
        ...updatedDashboard,
        ...data,
        updated_at: new Date().toISOString(),
      };
      
      set(state => ({
        dashboards: state.dashboards.map(d => d.id === id ? updated : d),
        currentDashboard: state.currentDashboard?.id === id ? updated : state.currentDashboard,
        isLoading: false,
      }));
      
      return updated;
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to update dashboard', isLoading: false });
      throw error;
    }
  },

  deleteDashboard: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      // TODO: Implement actual API call
      set(state => ({
        dashboards: state.dashboards.filter(d => d.id !== id),
        currentDashboard: state.currentDashboard?.id === id ? null : state.currentDashboard,
        isLoading: false,
      }));
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to delete dashboard', isLoading: false });
      throw error;
    }
  },

  duplicateDashboard: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      // TODO: Implement actual API call
      const original = get().dashboards.find(d => d.id === id);
      if (!original) {
        throw new Error('Dashboard not found');
      }
      
      const duplicate: Dashboard = {
        ...original,
        id: `dashboard-${Date.now()}`,
        name: `${original.name} (Copy)`,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      
      set(state => ({
        dashboards: [...state.dashboards, duplicate],
        isLoading: false,
      }));
      
      return duplicate;
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to duplicate dashboard', isLoading: false });
      throw error;
    }
  },

  addWidget: (widget: DashboardWidget) => {
    set(state => ({
      currentDashboard: state.currentDashboard ? {
        ...state.currentDashboard,
        layout: {
          ...state.currentDashboard.layout,
          widgets: [...state.currentDashboard.layout.widgets, widget],
        },
        updated_at: new Date().toISOString(),
      } : null,
    }));
  },

  updateWidget: (widgetId: string, updates: Partial<DashboardWidget>) => {
    set(state => ({
      currentDashboard: state.currentDashboard ? {
        ...state.currentDashboard,
        layout: {
          ...state.currentDashboard.layout,
          widgets: state.currentDashboard.layout.widgets.map(w => 
            w.id === widgetId ? { ...w, ...updates } : w
          ),
        },
        updated_at: new Date().toISOString(),
      } : null,
    }));
  },

  removeWidget: (widgetId: string) => {
    set(state => ({
      currentDashboard: state.currentDashboard ? {
        ...state.currentDashboard,
        layout: {
          ...state.currentDashboard.layout,
          widgets: state.currentDashboard.layout.widgets.filter(w => w.id !== widgetId),
        },
        updated_at: new Date().toISOString(),
      } : null,
    }));
  },

  moveWidget: (widgetId: string, position: { x: number; y: number }) => {
    get().updateWidget(widgetId, { position: { ...get().currentDashboard?.layout.widgets.find(w => w.id === widgetId)?.position!, ...position } });
  },

  resizeWidget: (widgetId: string, size: { w: number; h: number }) => {
    get().updateWidget(widgetId, { position: { ...get().currentDashboard?.layout.widgets.find(w => w.id === widgetId)?.position!, ...size } });
  },

  saveDashboard: async () => {
    const currentDashboard = get().currentDashboard;
    if (!currentDashboard) return;
    
    try {
      await get().updateDashboard(currentDashboard.id, { layout: currentDashboard.layout });
    } catch (error) {
      throw error;
    }
  },

  resetDashboard: () => {
    set({ currentDashboard: null, error: null });
  },
}));